import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import FitModal from './FitModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { Fits } from "../../../../types/Fits";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const Fit: React.FC = () => {
  const [fits, setFits] = useState<Fits[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Fits | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchFit = async () => {
    try {
      const data = await apiRequest<Fits[]>({
        method: 'get',
        url: 'fits',
      });
      setFits(data);
    } catch (error) {
      showError('Failed to fetch fit');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFit();
  }, []);

  const handleEditClick = (record: Fits) => {
    setIsEditMode(true);
    setInitialValues({
      fit_id: record.fit_id,
      fit_name: record.fit_name,
      fit_mesh_name: record.fit_mesh_name,
      fit_status: record.fit_status,
      fit_image: record.fit_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchFit();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (fit_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-fit/${fit_id}`,
      });
      showSuccess('Fit deleted');
      fetchFit();
    } catch {
      showError('Failed to delete fit');
    }
  };

  const handleStatusToggle = async (fitId: number) => {
    setFits((prev) =>
      prev.map((item) =>
        item.fit_id === fitId
          ? { ...item, fit_status: item.fit_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-fit/${fitId}`,
      });
      showSuccess('Fit status updated');
    } catch (error) {
      showError('Failed to update status');
      setFits((prev) =>
        prev.map((item) =>
          item.fit_id === fitId
            ? { ...item, fit_status: item.fit_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Fits>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Fits> = [
    
    {
      title: 'Fit',
      dataIndex: 'fit_name',
      key: 'fit_name',
      width: 500,
      filters: Array.from(new Set(fits.map((s) => s.fit_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.fit_name || null,
      onFilter: (value, record) => record.fit_name.includes(value as string),
      sorter: (a, b) => a.fit_name.localeCompare(b.fit_name),
      sortOrder: sortedInfo.columnKey === 'fit_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.fit_name}</div>,
    },
    {
      title: 'Fit Mesh Name',
      dataIndex: 'fit_mesh_name',
      key: 'fit_mesh_name',
      width: 500,
      filters: Array.from(new Set(fits.map((s) => s.fit_mesh_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.fit_mesh_name || null,
      onFilter: (value, record) => record.fit_mesh_name.includes(value as string),
      sorter: (a, b) => a.fit_mesh_name.localeCompare(b.fit_mesh_name),
      sortOrder: sortedInfo.columnKey === 'fit_mesh_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.fit_mesh_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'fit_image',
      key: 'fit_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.fit_image}
            alt="Fit"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Fits) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.fit_status}
                onChange={() => handleStatusToggle(record.fit_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this fit?"
              onConfirm={() => handleDelete(record.fit_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Fit</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Fit 
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Fits>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="fit_id"
          dataSource={fits.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Fit found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, fits.length)} of{' '}
                {fits.length}
              </span>
              <Pagination
                {...pagination}
                total={fits.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <FitModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        fits={fits}
      />
    </>
  );
};

export default Fit;